import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, CreditCard, CheckCircle2, Bell, Volume2, VolumeX, Sparkles, Ticket, Plus, Trash2, Copy, Shield, Lock, Users as UsersIcon, Calendar as CalendarIcon, Search, Bug, Lightbulb, MessageSquare, Mail } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ThemeSettings from "@/components/ThemeSettings";
import { useNotifications } from "@/context/NotificationsContext";

const emptyBiz = { company_name: "", company_email: "", company_address: "", company_phone: "", website: "", owner_name: "" };

export default function Settings() {
  const { user } = useAuth();
  const [biz, setBiz] = useState(emptyBiz);
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState(null);
  const [limits, setLimits] = useState(null);
  const [busySub, setBusySub] = useState(false);

  useEffect(() => {
    (async () => {
      const [b, s, l] = await Promise.all([
        api.get("/business"),
        api.get("/subscription/status").catch(() => ({ data: null })),
        api.get("/subscription/limits").catch(() => ({ data: null })),
      ]);
      setBiz({ ...emptyBiz, ...b.data });
      setSub(s.data);
      setLimits(l.data);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business", biz);
      toast.success("Business profile saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const field = (key, label, type = "input") => (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
      {type === "textarea"
        ? <Textarea rows={2} value={biz[key] || ""} onChange={(e) => setBiz({ ...biz, [key]: e.target.value })} data-testid={`biz-${key}`} />
        : <Input value={biz[key] || ""} onChange={(e) => setBiz({ ...biz, [key]: e.target.value })} data-testid={`biz-${key}`} />}
    </div>
  );

  return (
    <div data-testid="settings-page" className="max-w-4xl">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Settings</h1>
      <p className="text-sm text-slate-500 mt-1">Your business profile and integration status.</p>

      <div className="mt-6 space-y-4">
        <ThemeSettings />

        <NotificationPrefsCard />

        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="subscription-card">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-slate-500" />
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Subscription</div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display font-bold text-lg">
                {sub?.plan === "pro" ? "Quick Quotes Pro · $3.99/month" : "Free plan"}
              </div>
              <div className="text-sm text-slate-500 mt-0.5">
                {sub?.plan === "pro" ? (
                  sub?.status === "active" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Active</span>
                  ) : sub?.status === "past_due" ? (
                    <span className="text-amber-600">Past due — update payment method</span>
                  ) : sub?.status === "canceled" ? (
                    <span className="text-slate-600">Canceled</span>
                  ) : (
                    <span className="text-slate-500">Inactive</span>
                  )
                ) : (
                  limits ? <span data-testid="settings-usage">{limits.quotes_used} of {limits.quotes_limit} free quotes used</span> : "3 free quotes included — then upgrade"
                )}
                {sub?.cancel_at_period_end && sub?.current_period_end && (
                  <span className="ml-2 text-amber-600">Cancels on {new Date(sub.current_period_end * 1000).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            {sub?.status === "active" && !sub?.cancel_at_period_end && user?.role !== "admin" && (
              <Button variant="outline" disabled={busySub} data-testid="cancel-sub-btn" onClick={async () => {
                if (!window.confirm("Cancel at period end? You keep access until the end of this billing cycle.")) return;
                setBusySub(true);
                try { await api.post("/subscription/cancel"); const { data } = await api.get("/subscription/status"); setSub(data); toast.success("Subscription will cancel at period end"); }
                catch (e) { toast.error(formatError(e)); }
                finally { setBusySub(false); }
              }}>Cancel subscription</Button>
            )}
            {sub?.plan !== "pro" && user?.role !== "admin" && (
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
                window.location.href = data.checkout_url;
              }} data-testid="activate-sub-btn">Upgrade to Pro · $3.99/mo</Button>
            )}
          </div>
        </div>

        {user?.role === "admin" && <PromoCodesCard />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Business Profile</div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving} data-testid="save-biz">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("company_name", "Company name")}
            {field("owner_name", "Owner name")}
            {field("company_email", "Company email")}
            {field("company_phone", "Company phone")}
            {field("website", "Website")}
            <div />
            <div className="sm:col-span-2">{field("company_address", "Company address", "textarea")}</div>
          </div>
          <p className="px-5 pb-4 text-xs text-slate-500">Shown on every PDF quote and invoice you send.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="font-display font-bold mb-3">Account</div>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Name</dt><dd className="font-medium">{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-medium">{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Role</dt><dd className="font-medium capitalize">{user?.role}</dd></div>
          </dl>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="font-display font-bold mb-3">Integrations</div>
          <ul className="text-sm space-y-2">
            <li className="flex justify-between"><span>Email (Resend)</span><span className="status-pill status-draft">Configurable</span></li>
            <li className="flex justify-between"><span>SMS (Twilio)</span><span className="status-pill status-draft">Configurable</span></li>
            <li className="flex justify-between"><span>Payments (Stripe)</span><span className="status-pill status-draft">Configurable</span></li>
          </ul>
          <p className="mt-3 text-xs text-slate-500">Messages currently log to Communications as mock delivery until credentials are provided.</p>
        </div>
        </div>
      </div>

      <AdminSettingsCard />
    </div>
  );
}

function NotificationPrefsCard() {
  const { soundEnabled, toggleSound } = useNotifications();
  const [busy, setBusy] = useState(false);
  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/test");
      toast.success("Test notification sent — check the bell");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="notif-prefs-card">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-slate-500" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Notifications</div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display font-bold text-base text-slate-900">Notification sound</div>
          <div className="text-sm text-slate-500 mt-0.5">
            Play a short chime whenever a new notification arrives while you're signed in.
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleSound(!soundEnabled)}
          data-testid="notif-sound-toggle"
          aria-pressed={soundEnabled}
          className={`relative inline-flex h-7 w-12 items-center rounded-full border transition-colors ${
            soundEnabled ? "bg-blue-600 border-blue-600" : "bg-slate-200 border-slate-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              soundEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
          <span className="sr-only">Toggle notification sound</span>
        </button>
      </div>
      <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
        {soundEnabled ? <Volume2 className="w-4 h-4 text-blue-600" /> : <VolumeX className="w-4 h-4 text-slate-400" />}
        <span>{soundEnabled ? "Sound alerts are ON" : "Sound alerts are OFF"}</span>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-slate-900">Send a test notification</div>
          <div className="text-xs text-slate-500">Verifies real-time delivery, the red badge, and the sound.</div>
        </div>
        <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="notif-send-test-settings">
          <Sparkles className="w-4 h-4 mr-1.5" /> Send test
        </Button>
      </div>
    </div>
  );
}

function PromoCodesCard() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", description: "", max_uses: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/promo-codes"); setCodes(r.data || []); }
    catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toLowerCase();
    if (!code) return;
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code,
        description: form.description.trim(),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      });
      toast.success(`Code "${code}" created`);
      setForm({ code: "", description: "", max_uses: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete code "${c.code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const copyLink = (c) => {
    const url = `${window.location.origin}/signup?promo=${encodeURIComponent(c.code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Signup link copied");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="promo-codes-card">
      <div className="flex items-center gap-2 mb-4">
        <Ticket className="w-4 h-4 text-blue-600" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Promo Codes · Admin</div>
      </div>

      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_120px_auto] gap-2 mb-4" data-testid="promo-create-form">
        <Input
          placeholder="code (e.g. expo25)"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\s+/g, "") })}
          required
          data-testid="promo-code-input"
        />
        <Input
          placeholder="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="promo-desc-input"
        />
        <Input
          type="number"
          min="1"
          placeholder="Max uses"
          value={form.max_uses}
          onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
          data-testid="promo-max-input"
        />
        <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="promo-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add
        </Button>
      </form>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="text-sm text-slate-500">No promo codes yet. Add one above — share the signup link at trade shows, with partners, or in email blasts.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3 hidden sm:table-cell">Description</th>
                <th className="py-2 pr-3">Uses</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const exhausted = c.max_uses && c.uses >= c.max_uses;
                return (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`promo-row-${c.code}`}>
                    <td className="py-2.5 pr-3 font-mono font-bold text-slate-900">{c.code}</td>
                    <td className="py-2.5 pr-3 text-slate-600 hidden sm:table-cell">{c.description || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2.5 pr-3 font-mono">
                      {c.uses}
                      <span className="text-slate-400"> / {c.max_uses ?? "∞"}</span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <button
                        onClick={() => toggle(c)}
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          exhausted ? "bg-slate-100 text-slate-500" :
                          c.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" :
                          "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`promo-toggle-${c.code}`}
                        disabled={exhausted}
                      >
                        {exhausted ? "Used up" : c.active ? "Active" : "Paused"}
                      </button>
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => copyLink(c)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Copy signup link" data-testid={`promo-copy-${c.code}`}>
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(c)} className="p-1.5 text-slate-500 hover:text-rose-600" title="Delete" data-testid={`promo-delete-${c.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-500">
        Codes grant a free Pro account at signup. Share the per-code link to pre-fill it: <code className="text-slate-700">/signup?promo=&lt;code&gt;</code>
      </p>
    </div>
  );
}

function AdminSettingsCard() {
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem("qq_owner_token"));
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");

  const loadStats = async () => {
    const tok = sessionStorage.getItem("qq_owner_token");
    if (!tok) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/stats`, {
        headers: { "X-Owner-Token": tok },
      });
      if (r.status === 401) {
        sessionStorage.removeItem("qq_owner_token");
        setUnlocked(false);
        return;
      }
      const d = await r.json();
      setStats(d);
    } catch { toast.error("Could not load admin stats"); }
  };

  useEffect(() => { if (unlocked) loadStats(); /* eslint-disable-next-line */ }, [unlocked]);

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u.trim(), password: p }),
      });
      if (!r.ok) { toast.error("Incorrect admin credentials"); return; }
      const d = await r.json();
      sessionStorage.setItem("qq_owner_token", d.token);
      setUnlocked(true);
      setU(""); setP("");
      toast.success("Admin unlocked for this session");
    } catch { toast.error("Could not unlock admin"); }
    finally { setBusy(false); }
  };

  const lock = () => {
    sessionStorage.removeItem("qq_owner_token");
    setUnlocked(false);
    setStats(null);
    toast.message("Admin locked");
  };

  const filtered = stats?.recent_signups?.filter((m) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (m.name || "").toLowerCase().includes(needle) || (m.email || "").toLowerCase().includes(needle);
  }) || [];

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="admin-settings-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-slate-900 flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-slate-900">Admin Settings</div>
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
              {unlocked ? "Unlocked · this session" : "Owner access required"}
            </div>
          </div>
        </div>
        {unlocked && (
          <Button variant="outline" size="sm" onClick={lock} data-testid="admin-lock">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Lock
          </Button>
        )}
      </div>

      {!unlocked ? (
        <form onSubmit={unlock} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid="admin-unlock-form" autoComplete="off" spellCheck={false}>
          {/* Honeypot inputs to absorb browser/password-manager autofill */}
          <input type="text" name="username" autoComplete="username" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
          <input type="password" name="password" autoComplete="current-password" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Username</Label>
            <Input
              value={u}
              onChange={(e) => setU(e.target.value)}
              required
              name="qq-admin-user"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              data-testid="admin-username"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
            <Input
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              required
              name="qq-admin-pass"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              data-testid="admin-password"
            />
          </div>
          <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white h-10" data-testid="admin-unlock-submit">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Unlocking…" : "Unlock"}
          </Button>
          <p className="sm:col-span-3 text-[11px] text-slate-500">
            Only Quick Quotes owners have the credentials. Session expires in 12 hours or when you close the browser.
          </p>
        </form>
      ) : !stats ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div data-testid="admin-dashboard">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <StatTile label="Total Accounts" value={stats.total_accounts} icon={UsersIcon} tone="blue" testid="stat-total" />
            <StatTile label="New Today" value={stats.new_today} icon={CalendarIcon} tone="emerald" testid="stat-today" />
            <StatTile label="New This Month" value={stats.new_this_month} icon={CalendarIcon} tone="indigo" testid="stat-month" />
          </div>

          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or email…"
                className="pl-9"
                data-testid="admin-search"
              />
            </div>
            <div className="text-xs text-slate-500 whitespace-nowrap">
              {filtered.length} / {stats.recent_signups.length} shown
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-recent-table">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3 hidden sm:table-cell">Email</th>
                  <th className="py-2 pr-3 hidden md:table-cell">Plan</th>
                  <th className="py-2">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr key={m.email + i} className="border-b border-slate-100" data-testid={`admin-row-${i}`}>
                    <td className="py-2.5 pr-3 font-semibold text-slate-900 whitespace-nowrap">
                      {m.name}
                      <div className="sm:hidden text-xs font-normal text-slate-500 mt-0.5">{m.email}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600 hidden sm:table-cell">{m.email}</td>
                    <td className="py-2.5 pr-3 hidden md:table-cell">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        m.plan === "pro" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"
                      }`}>{m.plan}</span>
                    </td>
                    <td className="py-2.5 text-xs text-slate-500 whitespace-nowrap">
                      {m.signup_date ? new Date(m.signup_date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-sm text-slate-500">No accounts match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Read-only view · Signups refresh when you re-open this card.
          </p>

          <AdminFeedbackSection />
        </div>
      )}
    </div>
  );
}

function AdminFeedbackSection() {
  const [items, setItems] = useState(null);
  const [totals, setTotals] = useState(null);
  const [filter, setFilter] = useState("all"); // all | bug | suggestion | unread

  const load = async () => {
    const tok = sessionStorage.getItem("qq_owner_token");
    if (!tok) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/feedback`, {
        headers: { "X-Owner-Token": tok },
      });
      if (!r.ok) { toast.error("Could not load feedback"); return; }
      const d = await r.json();
      setItems(d.items || []);
      setTotals(d.totals || null);
    } catch { toast.error("Could not load feedback"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id, status) => {
    const tok = sessionStorage.getItem("qq_owner_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/feedback/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Owner-Token": tok },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error();
      await load();
    } catch { toast.error("Could not update"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feedback entry?")) return;
    const tok = sessionStorage.getItem("qq_owner_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/feedback/${id}`, {
        method: "DELETE",
        headers: { "X-Owner-Token": tok },
      });
      if (!r.ok) throw new Error();
      toast.success("Deleted");
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const filtered = (items || []).filter((it) => {
    if (filter === "all") return true;
    if (filter === "unread") return it.status === "new";
    return it.type === filter;
  });

  return (
    <div className="mt-8 pt-6 border-t border-slate-200" data-testid="admin-feedback-section">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-amber-500 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-slate-900">User Feedback</div>
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
              Bug reports & suggestions
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="feedback-refresh">
          Refresh
        </Button>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatTile label="Total" value={totals.total} icon={MessageSquare} tone="indigo" testid="fb-stat-total" />
          <StatTile label="Bugs" value={totals.bugs} icon={Bug} tone="rose" testid="fb-stat-bugs" />
          <StatTile label="Suggestions" value={totals.suggestions} icon={Lightbulb} tone="amber" testid="fb-stat-suggestions" />
          <StatTile label="Unread" value={totals.unread} icon={Mail} tone="blue" testid="fb-stat-unread" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[
          { k: "all", label: "All" },
          { k: "unread", label: "Unread" },
          { k: "bug", label: "Bugs" },
          { k: "suggestion", label: "Suggestions" },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`px-3 h-8 rounded-full text-xs font-bold uppercase tracking-wider border ${
              filter === k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`fb-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="text-sm text-slate-500">Loading feedback…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center bg-slate-50 border border-slate-200 rounded-md">
          No feedback {filter !== "all" ? "in this filter" : "yet"}.
        </div>
      ) : (
        <div className="space-y-2" data-testid="fb-list">
          {filtered.map((it) => (
            <div
              key={it.id}
              className={`border rounded-md p-3 ${
                it.status === "new" ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50"
              }`}
              data-testid={`fb-row-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      it.type === "bug" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {it.type === "bug" ? <Bug className="w-3 h-3" /> : <Lightbulb className="w-3 h-3" />}
                    {it.type}
                  </span>
                  {it.status === "new" && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700">
                      New
                    </span>
                  )}
                  <span className="text-[11px] text-slate-500">
                    {it.created_at ? new Date(it.created_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {it.status === "new" ? (
                    <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => markRead(it.id, "read")} data-testid={`fb-mark-read-${it.id}`}>
                      Mark read
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => markRead(it.id, "new")} data-testid={`fb-mark-unread-${it.id}`}>
                      Mark unread
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => del(it.id)} data-testid={`fb-delete-${it.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-[12px] text-slate-500 mb-1">
                From: <strong className="text-slate-700">{it.from_name || "Anonymous"}</strong>
                {it.from_email ? (
                  <> &middot; <a href={`mailto:${it.from_email}`} className="text-blue-600 hover:underline">{it.from_email}</a></>
                ) : null}
                {it.page_url ? (
                  <> &middot; <a href={it.page_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:underline">page</a></>
                ) : null}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{it.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    indigo: "bg-indigo-50 text-indigo-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone] || tones.blue}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{label}</div>
        <div className="font-display text-2xl font-extrabold text-slate-900 leading-none">{value}</div>
      </div>
    </div>
  );
}


